import React, { Fragment } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { siteMap } from "internal-sitemap";
import { CollectionUtils } from "utilities/collection-utils";
import { CoreUtils } from "utilities/core-utils";
import { RouteDefinition } from "utilities/interfaces/route-definition";
import { RouteUtils } from "utilities/route-utils";
import AuthenticatedRoute from "utilities/routing/authenticated-route";

/*
---------------------------------------------------------------------------------------------
Interfaces
---------------------------------------------------------------------------------------------
*/

export interface NestedRoutesProps {
    /**
     * If none of the the supplied routes are matched,
     * redirect to not-found (404) error page.
     * @default false
     */
    redirectIfNotFound?: boolean;
    routes: RouteDefinition[];
}

export interface NestedRoutesByPropertyProps {
    propertyName: keyof RouteDefinition;
    routes: RouteDefinition[];
}

/*
---------------------------------------------------------------------------------------------
Components
---------------------------------------------------------------------------------------------
*/

/**
 * Dynamically renders a route and its subroutes, accounting
 * for additional custom properties on RouteDefinition
 */
export const NestedRoute = (route: any) => {
    const RouteComponent: any = route.authRequired ? AuthenticatedRoute : Route;
    const childRoutes = CoreUtils.objectToArray(route.routes).map(
        (route: RouteDefinition) =>
            RouteUtils.withCulture<RouteDefinition>(route)
    );
    const localizedRoute = RouteUtils.withCulture<RouteDefinition>(route);

    return (
        <RouteComponent
            exact={localizedRoute.exact}
            path={localizedRoute.path}
            route={localizedRoute}
            render={(props: any) => (
                // pass the sub-routes down to keep nesting
                <route.component {...props} routes={childRoutes} />
            )}
        />
    );
};

/**
 * Component to easily render nested sub-route components from a list of routes.
 * Commonly used when setting up a layout
 */
export const NestedRoutes = (props: NestedRoutesProps) => {
    if (CollectionUtils.isEmpty(props.routes)) {
        return null;
    }

    return (
        <Fragment>
            <Switch>
                {props.routes.map((route: any, i: any) => (
                    <NestedRoute key={i} {...route} />
                ))}
                {
                    //if
                    props.redirectIfNotFound === true && (
                        <Redirect to={siteMap.errors.notFound} />
                    )
                }
            </Switch>
        </Fragment>
    );
};

/**
 * Renders Route components mapped to a custom property
 */
export const NestedRoutesByProperty = (props: NestedRoutesByPropertyProps) => {
    if (CollectionUtils.isEmpty(props.routes)) {
        return null;
    }

    return (
        <Fragment>
            {props.routes.map((route: any, i: any) => {
                const component = route[props.propertyName];

                if (component == null) {
                    return null;
                }

                return (
                    <Route
                        key={i}
                        path={route.path}
                        exact={route.exact}
                        component={component}
                    />
                );
            })}
        </Fragment>
    );
};
